import React, { useCallback, useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { RoleContext } from "../../RoleContext"; // Import RoleContext
import Logo from "../../assets/img/logo.png";

const Sidebar = () => {
  const { user } = useContext(RoleContext); // Access user from RoleContext
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState("dashboard");
  const [isVisitorDropdownOpen, setVisitorDropdownOpen] = useState(false);
  const navigate = useNavigate();

  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
    document.body.style.overflow = isSidebarOpen ? "auto" : "hidden";
  };

  const handleLogout = useCallback(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("loginTimestamp");
    navigate("/");
  }, [navigate]);

  const handleMenuClick = (menu, path) => {
    setSelectedMenu(menu);
    navigate(path);
    if (menu !== "visitor") {
      setVisitorDropdownOpen(false);
    }
  };

  const toggleVisitorDropdown = () => {
    setVisitorDropdownOpen(!isVisitorDropdownOpen);
    setSelectedMenu("visitor");
  };

  const handleSubmenuClick = (submenu, path) => {
    setSelectedMenu(submenu);
    navigate(path);
  };

  useEffect(() => {
    const loginTimestamp = localStorage.getItem("loginTimestamp");
    if (loginTimestamp) {
      const elapsedTime = Date.now() - parseInt(loginTimestamp, 10);
      const twentyFourHours = 24 * 60 * 60 * 1000;
      if (elapsedTime >= twentyFourHours) {
        handleLogout();
      } else {
        const remainingTime = twentyFourHours - elapsedTime;
        setTimeout(() => handleLogout(), remainingTime);
      }
    }
  }, [handleLogout]);

  // Define menu items with their roles
  const menuItems = [
    {
      name: "dashboard",
      path: "/dashboard",
      icon: "fas fa-th-large",
      label: "Dashboard",
      roles: ["admin", "staff", "super-admin"],
    },
    {
      name: "visitor",
      icon: "fas fa-users",
      label: "Visitor",
      roles: ["admin", "staff", "super-admin"],
      isDropdown: true,
      subItems: [
        {
          name: "visitorForm",
          path: "/register",
          icon: "fas fa-user-circle",
          label: "Visitor Form",
          roles: ["admin", "staff", "super-admin"],
        },
        {
          name: "activeVisitors",
          path: "/visitors",
          icon: "fas fa-door-open",
          label: "Active Visitors",
          roles: ["admin", "staff", "super-admin"],
        },
      ],
    },
    {
      name: "visitorProblems",
      path: "/visitor-problems",
      icon: "fas fa-comments",
      label: "Visitors Problem",
      roles: ["admin", "staff", "super-admin"],
    },
    {
      name: "visitorAnalytics",
      path: "/visitornalytics",
      icon: "fas fa-chart-line",
      label: "Visitor Analytics",
      roles: ["admin", "staff", "super-admin"],
    },
    {
      name: "broadcast",
      path: "/broadcast",
      icon: "fas fa-broadcast-tower",
      label: "Broadcast",
      roles: ["admin", "staff", "super-admin"],
    },
    {
      name: "createCategory",
      path: "/createCategory",
      icon: "fas fa-plus",
      label: "Create Category",
      roles: ["super-admin"],
    },
    {
      name: "office",
      path: "/office",
      icon: "fas fa-building",
      label: "Create Office",
      roles: ["super-admin"],
    },
  ];

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(
    (item) => !user || item.roles.includes(user?.role)
  );

  return (
    <>
      {/* Mobile Hamburger Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 bg-[#068800] text-white p-2 rounded-md"
        onClick={toggleSidebar}
      >
        <i className={`fas ${isSidebarOpen ? "fa-times" : "fa-bars"}`}></i>
      </button>

      {/* Sidebar + Overlay */}
      <div
        className={`fixed inset-0 z-40 ${
          isSidebarOpen ? "block" : "hidden"
        } lg:hidden`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={toggleSidebar}
        ></div>

        {/* Sidebar */}
        <div
          className={`absolute top-0 left-0 bg-[#0b0b33] text-white w-64 h-full transform transition-transform ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0`}
        >
          <div className="mt-10 text-3xl font-bold mb-12 text-center">
            <img src={Logo} alt="Logo" className="w-32 mx-auto" />
          </div>
          <div className="w-full px-4">
            {filteredMenuItems.map((item) =>
              item.isDropdown ? (
                <div key={item.name} className="w-full">
                  <div
                    className={`flex items-center mb-6 cursor-pointer ${
                      selectedMenu === item.name
                        ? "bg-[#068800] p-2 rounded-md"
                        : ""
                    }`}
                    onClick={toggleVisitorDropdown}
                  >
                    <i className={`${item.icon} mr-2`}></i>
                    <span>{item.label}</span>
                    <i
                      className={`fas fa-chevron-${
                        isVisitorDropdownOpen ? "up" : "down"
                      } ml-auto`}
                    ></i>
                  </div>
                  {isVisitorDropdownOpen && (
                    <div className="ml-6">
                      {item.subItems
                        .filter(
                          (subItem) =>
                            !user || subItem.roles.includes(user?.role)
                        )
                        .map((subItem) => (
                          <div
                            key={subItem.name}
                            className={`flex items-center mb-4 cursor-pointer ${
                              selectedMenu === subItem.name
                                ? "bg-[#068800] p-2 rounded-md"
                                : ""
                            }`}
                            onClick={() =>
                              handleSubmenuClick(subItem.name, subItem.path)
                            }
                          >
                            <i className={`${subItem.icon} mr-2`}></i>
                            <span>{subItem.label}</span>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  key={item.name}
                  className={`flex items-center mb-6 cursor-pointer ${
                    selectedMenu === item.name
                      ? "bg-[#068800] p-2 rounded-md"
                      : ""
                  }`}
                  onClick={() => handleMenuClick(item.name, item.path)}
                >
                  <i className={`${item.icon} mr-2`}></i>
                  <span>{item.label}</span>
                </div>
              )
            )}
          </div>
          <div className="w-full px-4 mt-auto">
            <button
              onClick={handleLogout}
              className="w-full bg-[#068800] text-white py-2 rounded-md mt-6"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block bg-[rgba(53,143,49,0.71)] text-white w-64 h-full">
        <div className="mt-10 text-3xl font-bold mb-12 text-center">
          <img src={Logo} alt="Logo" className="w-32 mx-auto" />
        </div>
        <div className="w-full px-4">
          {filteredMenuItems.map((item) =>
            item.isDropdown ? (
              <div key={item.name} className="w-full">
                <div
                  className={`flex items-center mb-6 cursor-pointer ${
                    selectedMenu === item.name
                      ? "bg-[#068800] p-2 rounded-md"
                      : ""
                  }`}
                  onClick={toggleVisitorDropdown}
                >
                  <i className={`${item.icon} mr-2`}></i>
                  <span>{item.label}</span>
                  <i
                    className={`fas fa-chevron-${
                      isVisitorDropdownOpen ? "up" : "down"
                    } ml-auto`}
                  ></i>
                </div>
                {isVisitorDropdownOpen && (
                  <div className="ml-6">
                    {item.subItems
                      .filter(
                        (subItem) => !user || subItem.roles.includes(user?.role)
                      )
                      .map((subItem) => (
                        <div
                          key={subItem.name}
                          className={`flex items-center mb-4 cursor-pointer ${
                            selectedMenu === subItem.name
                              ? "bg-[#068800] p-2 rounded-md"
                              : ""
                          }`}
                          onClick={() =>
                            handleSubmenuClick(subItem.name, subItem.path)
                          }
                        >
                          <i className={`${subItem.icon} mr-2`}></i>
                          <span>{subItem.label}</span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                key={item.name}
                className={`flex items-center mb-6 cursor-pointer ${
                  selectedMenu === item.name
                    ? "bg-[#068800] p-2 rounded-md"
                    : ""
                }`}
                onClick={() => handleMenuClick(item.name, item.path)}
              >
                <i className={`${item.icon} mr-2`}></i>
                <span>{item.label}</span>
              </div>
            )
          )}
        </div>
        <div className="w-full px-4 mt-auto">
          <button
            onClick={handleLogout}
            className="w-full bg-[#068800] text-white py-2 rounded-md mt-6"
          >
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
